import { Controller } from "@hotwired/stimulus";

const EMBED_CHECK_INTERVAL = 100;
const EMBED_FAILURE_WARN_EVERY_X_SECONDS = 2;

export default class extends Controller {
  static targets = ["loader", "embed"];

  #embedIntervalTimeout;

  connect() {
    console.log("SavvyCal embed controller connected");
    this.#showLoader();
    this.#startEmbedChecks();
  }

  disconnect() {
    this.#cancelEmbedChecks();
  }

  handleAppointmentCreated(event) {
    try {
      const savvycalId = event.detail && event.detail.appointmentId;
      if (savvycalId) {
        const params = new URLSearchParams({
          savvycal_id: savvycalId,
        });
        window.location.href = `/thank_you?${params.toString()}`;
      } else {
        window.location.href = "/thank_you";
      }
    } catch (error) {
      console.error("Error handling appointment created event:", error);
    } finally {
      // Just in case there is some kind of redirection failure, hide the embed
      // to prevent the user from booking a second time
      this.#hideEmbed();
    }
  }

  #showLoader() {
    this.loaderTarget.classList.remove("hidden");
  }

  #hideLoader() {
    this.loaderTarget.classList.add("hidden");
  }

  #startEmbedChecks() {
    // Abort immediately if the embed is already loaded
    if (this.#checkForEmbed()) {
      return;
    }

    // Set up tracking vars
    let iterations = 0;
    const warningInterval = Math.floor(
      (EMBED_FAILURE_WARN_EVERY_X_SECONDS * 1000) / EMBED_CHECK_INTERVAL
    ); // Warn every X seconds

    // Start periodic checks
    this.#embedIntervalTimeout = setInterval(() => {
      iterations++;

      // Check if the embed is loaded
      if (this.#checkForEmbed()) {
        return;
      }

      // Every once in a while log a warning to help us debug when customers don't see the embed
      if (iterations % warningInterval === 0) {
        this.#logEmbedCheckWarning(iterations);
      }
    }, EMBED_CHECK_INTERVAL);
  }

  #logEmbedCheckWarning(currentIteration) {
    console.warn(
      `SavvyCal embed still loading after ${
        (currentIteration * EMBED_CHECK_INTERVAL) / 1000
      } seconds`
    );

    // Add a signal to the element iteself so we can inspect this in Posthog
    this.embedTarget.dataset.loadingCheckIteration = currentIteration;
  }

  #checkForEmbed() {
    // SavvyCal will attach the iframe in a shadow root to the element
    const savvycalElement = this.embedTarget.querySelector(
      "savvycal-booking-embed"
    );
    if (!savvycalElement?.shadowRoot) {
      return false;
    }
    this.#cancelEmbedChecks();
    this.#hideLoader();
    return true;
  }

  #cancelEmbedChecks() {
    if (this.#embedIntervalTimeout) {
      clearInterval(this.#embedIntervalTimeout);
      this.#embedIntervalTimeout = null;
    }
  }

  #hideEmbed() {
    if (this.hasEmbedTarget) {
      this.embedTarget.classList.add("hidden");
    }
  }
}
